import React, { useEffect, useRef, useState } from 'react'
import {
  Avatar as MuiAvatar,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  makeStyles,
  Typography,
  CircularProgress
} from '@material-ui/core'
import { FileUploadOutlined, DeleteOutlined, InfoRounded } from '@material-ui/icons'
import { storageRef } from '../index'
import { Box, Fade } from '@mui/material'
import Tooltip from '@material-ui/core/Tooltip'
import { subjectReason } from 'constants/index'
import { toast } from 'react-toastify'

const UploadStatus = {
  uploading: 'Uploading',
  removing: 'Removing'
}

const AvatarPicker = (props) => {
  const {
    title,
    subTitle,
    path,
    imageName,
    resetImage = false,
    conferenceId,
    handleCustomChange,
    infoText,
    isSubject = false,
    onDelete,
    onUpload,
    firsTimeUpload,
    reason,
    imageProps = {}
  } = props
  const classes = useStyles()
  const fileInputRef = useRef()
  const [image, setImage] = useState(null)
  const [currentImage, setCurrentImage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [enableAction, setEnableAction] = useState(false)
  const [uploadStatus, setUploadStatus] = useState(null)

  const downloadImage = async () => {
    try {
      setLoading(true)
      const imageUrl = isSubject
        ? await storageRef.child(`subjects/${imageName}`).getDownloadURL()
        : await storageRef.child(`${conferenceId}/${path}/${imageName}`).getDownloadURL()
      setImage(imageUrl)
      setCurrentImage(imageName)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (imageName) {
      downloadImage()
    }
  }, [imageName])

  useEffect(() => {
    if (resetImage) {
      setCurrentImage(null)
      setImage(null)
    }
    return () => {
      setCurrentImage(null)
      setImage(null)
    }
  }, [resetImage])

  const handleChange = async (event) => {
    const file = event.target.files[0]
    if (!file) return
    const allowedFileTypes = ['image/jpg', 'image/jpeg', 'image/png']
    if (!allowedFileTypes.includes(file.type)) {
      toast.warning('Only JPG, JPEG and PNG files are allowed')
      return
    }
    setImage(URL.createObjectURL(file))
    event.target.value = null
    if (firsTimeUpload) {
      handleCustomChange(file)
    } else {
      setUploadStatus(UploadStatus.uploading)
      setEnableAction(true)
      const timestamp = new Date().getTime() + ''
      if (isSubject && reason === subjectReason.afterApproval) {
        await storageRef.child(`subjects/${timestamp}`).put(file)
        await storageRef.child(`${conferenceId}/${path}/${timestamp}`).put(file)
      } else if (isSubject) {
        await storageRef.child(`subjects/${timestamp}`).put(file)
      } else {
        await storageRef.child(`${conferenceId}/${path}/${timestamp}`).put(file)
      }
      await onUpload(timestamp)
      setCurrentImage(timestamp)
      setEnableAction(false)
      setUploadStatus(null)
    }
  }

  const handleDeleteFile = async () => {
    setEnableAction(true)
    setUploadStatus(UploadStatus.removing)
    if (isSubject && reason === subjectReason.afterApproval) {
      await storageRef.child(`subjects/${currentImage}`).delete()
      await storageRef.child(`${conferenceId}/${path}/${currentImage}`).delete()
    } else if (isSubject) {
      await storageRef.child(`subjects/${currentImage}`).delete()
    } else {
      await storageRef.child(`${conferenceId}/${path}/${currentImage}`).delete()
    }
    await onDelete()
    setEnableAction(false)
    setUploadStatus(null)
  }

  const buttonClickHandler = async () => {
    if (currentImage || image) {
      !firsTimeUpload && (await handleDeleteFile())
      setImage(null)
      setCurrentImage(null)
    } else {
      fileInputRef.current.click()
    }
  }

  return (
    <>
      <Box className={classes.heading}>
        <Typography variant="h5" color="secondary">
          {title}
        </Typography>
        {infoText && (
          <Tooltip
            arrow
            TransitionComponent={Fade}
            title={<Typography className={classes.tooltip}>{infoText}</Typography>}>
            <IconButton className={classes.infoIconBtn}>
              <InfoRounded className={classes.infoIcon} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="body2" color="textSecondary" className={classes.subTitle}>
        {subTitle}
      </Typography>

      <Card variant="elevation">
        <CardContent className={classes.cardContent}>
          {loading ? (
            <Box className={classes.loadingBox}>
              <CircularProgress />
            </Box>
          ) : (
            <MuiAvatar
              variant="square"
              imgProps={imageProps}
              className={classes.image}
              src={image}
            />
          )}
        </CardContent>
        <Divider />
        <Box>
          <input hidden accept="image/*" type="file" onChange={handleChange} ref={fileInputRef} />
          <Button
            fullWidth
            disabled={enableAction || loading}
            color="primary"
            variant="text"
            className={classes.button}
            startIcon={
              enableAction ? (
                <CircularProgress size={15} style={{ marginRight: 10 }} />
              ) : currentImage || image ? (
                <DeleteOutlined />
              ) : (
                <FileUploadOutlined />
              )
            }
            onClick={buttonClickHandler}>
            {uploadStatus ? uploadStatus : image || currentImage ? 'Remove' : 'Upload Image'}
          </Button>
        </Box>
      </Card>
    </>
  )
}

export default AvatarPicker

const useStyles = makeStyles({
  heading: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    marginBottom: '8px'
  },
  tooltip: {
    padding: '3px',
    fontSize: '13px'
  },
  infoIcon: {
    color: '#1e88e5'
  },
  infoIconBtn: {
    padding: '2px'
  },
  button: {
    padding: '10px'
  },
  image: {
    height: '140px',
    width: '150px',
    margin: '16px'
  },
  loadingBox: {
    height: '130px',
    width: '150px',
    margin: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'center'
  },
  subTitle: { marginBottom: 10, fontSize: '13px', fontStyle: 'italic' }
})
